import React, { FunctionComponent } from "react";
import { CustomRadioSelector, DisabledOverlay, SelectItemsComponent, TextArea } from "@iventis/components";
import { styled, fontSizes, media, muiInputFormsCSS } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { FormLabel } from "@mui/material";
import { PdfExportImageUploadComponent } from "./pdf-export-image-upload";
import { FormOptions, SelectedFormOption } from "./export-map-dialog";

interface ExportMapFormProps {
    formOptions: FormOptions;
    handleChange: ({ id, name }: { id: string; name: string }) => void;
    selectedFormOption: SelectedFormOption;
}

export const ExportMapForm: FunctionComponent<ExportMapFormProps> = ({ formOptions, handleChange, selectedFormOption }) => (
    <ExportMapFormContainer>
        {formOptions.map((option) => (
            <StyledWrapper key={option.id}>
                <StyledLabel>
                    <FormLabel>{option.title}</FormLabel>
                </StyledLabel>
                {option.disabled && <DisabledOverlay />}
                {option.type === "radio" && (
                    <CustomRadioSelector
                        options={option.optionValues}
                        onValueChange={(_, value) => handleChange({ id: option.id, name: value })}
                        row
                        defaultValueId={selectedFormOption[option.id]}
                        ref={option.ref}
                    />
                )}
                {option.type === "select" && (
                    <>
                        <SelectItemsComponent
                            options={option.optionValues}
                            value={selectedFormOption[option.id]}
                            changeValue={(value) => handleChange({ id: option.id, name: value })}
                            testId={option.id}
                            disabled={option.disabled}
                        />
                    </>
                )}
                {option.type === "file-upload" && (
                    <PdfExportImageUploadComponent
                        fileName=""
                        imageUrl={selectedFormOption.customLogo}
                        handleChange={(value) => handleChange({ id: option.id, name: value })}
                        maxFileSize={15}
                    />
                )}
                {option.type === "text-area" && (
                    <TextArea
                        dataTestId="export-remarks"
                        placeholder={option.placeholder}
                        rows={3}
                        error={option.error}
                        onChange={(value) => handleChange({ id: option.id, name: value })}
                    />
                )}
                {option.type === "text" && (
                    <TextArea
                        dataTestId="export-title"
                        placeholder={option.placeholder}
                        rows={1}
                        error={option.error}
                        value={selectedFormOption[option.id]}
                        onChange={(value) => handleChange({ id: option.id, name: value })}
                    />
                )}
            </StyledWrapper>
        ))}
    </ExportMapFormContainer>
);

const StyledWrapper = styled.div`
    overflow: auto;
    margin: 20px 0;
    position: relative;

    :first-of-type {
        margin-top: 0;
    }

    ${muiInputFormsCSS};
    .MuiInputBase-input {
        margin: 10px 0;
    }
`;

const StyledLabel = styled.label`
    display: flex;
    flex-direction: column;
    ${media.medium} {
        flex-direction: row;
    }

    .MuiFormLabel-root {
        font-size: ${fontSizes.medium};
        line-height: ${fontSizes.xLarge};
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core};
        font-weight: 600;
    }
`;

const ExportMapFormContainer = styled.div`
    min-width: 150px;
    overflow-x: auto;
    ${media.medium} {
        padding-right: 20px;
    }
`;
